import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useSmartPoll } from "../lib/useSmartPoll";
import { zVar } from "../lib/zIndex";
import { InfoTip } from "../components/InfoTip";
import { formatKSA, formatKSATime } from "../lib/ksaTime";

// ─── v119.218 — 📬 Outbound message log with delivery/read receipts ─────────
const MSG_STATUS = {
  read:      { icon: "✓✓", color: "#34B7F1", label: "قُرئت" },
  delivered: { icon: "✓✓", color: "#8a8a8a", label: "وصلت" },
  sent:      { icon: "✓",  color: "#8a8a8a", label: "أُرسلت" },
  failed:    { icon: "✗",  color: "#dc2626", label: "فشلت" },
  pending:   { icon: "⏳", color: "#b45309", label: "قيد الإرسال" },
};

function MessageLogPanel() {
  const [statusF, setStatusF] = useState("");
  const [q, setQ] = useState("");
  const [fromTs, setFromTs] = useState("");
  const [toTs, setToTs] = useState("");
  const [campaign, setCampaign] = useState("");
  const [sender, setSender] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(150);
  // v200.15 — raw-document viewer modal
  const [rawDoc, setRawDoc] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);

  // v-smoothpoll — interaction refs so background refresh pauses while the
  // user is reading (scroll / search / raw-doc modal) instead of rebuilding
  // the whole table under their hand.
  const scrollRef = useRef(null);
  const scrollingRef = useRef(false);
  const searchFocusRef = useRef(false);
  const scrollStopTimer = useRef(null);
  const rawDocRef = useRef(null);
  rawDocRef.current = rawDoc;

  // Convert `datetime-local` value (naive, treated as browser local)
  // into a UTC ISO string matching `whatsapp_logs.created_at` format.
  const toIso = (v) => {
    if (!v) return "";
    try { return new Date(v).toISOString(); } catch { return ""; }
  };

  const fetcher = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page), page_size: String(pageSize),
    });
    if (statusF) params.set("status", statusF);
    if (q.trim()) params.set("q", q.trim());
    const fIso = toIso(fromTs); if (fIso) params.set("from_ts", fIso);
    const tIso = toIso(toTs);   if (tIso) params.set("to_ts", tIso);
    if (campaign.trim()) params.set("campaign", campaign.trim());
    if (sender.trim())   params.set("sender",   sender.trim());
    const r = await api.get(`/whatsapp/message-log?${params.toString()}`);
    return r.data || {};
  }, [statusF, q, fromTs, toTs, campaign, sender, page, pageSize]);

  const isBusy = useCallback(() => (
    scrollingRef.current
    || (scrollRef.current?.scrollTop || 0) > 8
    || searchFocusRef.current
    || !!rawDocRef.current
  ), []);

  const {
    data, loading, lastUpdated, pending, pendingCount, refresh, applyPending,
  } = useSmartPoll({
    intervalMs: 30000,
    fetcher,
    getSignature: (d) => {
      const it = d?.items || [];
      return `${d?.total || 0}|${it.length}|${it[0]?.created_at || ""}|${it[it.length - 1]?.created_at || ""}`;
    },
    getNewCount: (next, prev) => Math.max(0, Number(next?.total || 0) - Number(prev?.total || 0)),
    isBusy,
    onError: (e) => toast.error(e?.response?.data?.detail || "تعذّر جلب سجل الرسائل"),
  });

  // Foreground (loading) refresh on first mount and whenever a filter/page changes.
  useEffect(() => { refresh(); }, [refresh]);

  const items = data?.items || [];
  const counts = data?.counts || {};
  const total = Number(data?.total || 0);
  const defaultPageSize = Number(data?.default_page_size || 150);

  const onTableScroll = useCallback(() => {
    scrollingRef.current = true;
    if (scrollStopTimer.current) clearTimeout(scrollStopTimer.current);
    scrollStopTimer.current = setTimeout(() => { scrollingRef.current = false; }, 1500);
  }, []);

  const fmtT = (iso) => formatKSA(iso, { withSeconds: false });
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  // v200.15 — fetch the full raw document for a single row using the
  // row's created_at timestamp as a ±2s window (whatsapp_logs.created_at
  // has millisecond precision; a narrow window plus the phone match
  // pinpoints the exact document without adding an id column).
  const openRawDoc = async (row) => {
    if (!row?.created_at) {
      toast.error("الصف بلا created_at — لا يمكن تحديد الوثيقة");
      return;
    }
    setRawLoading(true);
    try {
      const ts = new Date(row.created_at);
      const from = new Date(ts.getTime() - 2000).toISOString();
      const to   = new Date(ts.getTime() + 2000).toISOString();
      const r = await api.get(
        `/whatsapp/message-log/raw?from_ts=${encodeURIComponent(from)}&to_ts=${encodeURIComponent(to)}&limit=10`
      );
      const docs = r.data?.items || [];
      // Prefer exact phone match; else the closest by created_at.
      const rowPhone = row.phone || "";
      const exact = docs.find((d) => (d.phone || d.to) === rowPhone);
      setRawDoc(exact || docs[0] || { _error: "لم يُعثر على أي وثيقة في النافذة الزمنية" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر جلب الوثيقة الخام");
    } finally { setRawLoading(false); }
  };

  const resetFilters = () => {
    setStatusF(""); setQ(""); setFromTs(""); setToTs("");
    setCampaign(""); setSender(""); setPage(1);
  };

  return (
    <div data-testid="wa-msglog-panel">
      <div className="card" style={{ padding: 12, marginBottom: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "var(--brown-deep)" }}>📬 سجل الرسائل الصادرة</div>
        {[["", "الكل"], ["read", "✓✓ قُرئت"], ["delivered", "✓✓ وصلت"], ["sent", "✓ أُرسلت"], ["failed", "✗ فشلت"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => { setStatusF(v); setPage(1); }}
            data-testid={`msglog-filter-${v || "all"}`}
            style={{ padding: "4px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                     border: statusF === v ? "1.5px solid var(--brown-deep)" : "1px solid var(--border)",
                     background: statusF === v ? "var(--brown-deep)" : "#fff",
                     color: statusF === v ? "#fff" : "var(--muted)" }}>
            {l}{v && counts[v] != null ? ` (${counts[v]})` : ""}
          </button>
        ))}
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
               onFocus={() => { searchFocusRef.current = true; }}
               onBlur={() => { searchFocusRef.current = false; }}
               placeholder="🔍 اسم أو رقم..."
               className="sim-input" style={{ fontSize: 12, minWidth: 150 }} data-testid="msglog-search" />
        <div style={{ marginInlineStart: "auto", fontSize: 11, color: "var(--muted)", display: "flex", alignItems: "center", gap: 8 }}>
          <span>{loading ? "⏳..." : `${items.length} من أصل ${total.toLocaleString()}`}</span>
          <span style={{ color: "#9ca3af" }} data-testid="msglog-last-updated">
            {lastUpdated ? `آخر تحديث ${formatKSATime(lastUpdated)} (KSA)` : "—"}
          </span>
          <button type="button" onClick={() => refresh()} disabled={loading}
                  data-testid="msglog-refresh"
                  style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "#fff", color: "var(--brown-deep)" }}>
            ↻ تحديث
          </button>
        </div>
      </div>

      {/* v200.13 — Time-window + campaign + sender filters + page size */}
      <div className="card" style={{ padding: 10, marginBottom: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", fontSize: 11.5 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ color: "var(--muted)", fontWeight: 700 }}>من (تاريخ ووقت)</span>
          <input type="datetime-local" value={fromTs}
                 onChange={(e) => { setFromTs(e.target.value); setPage(1); }}
                 className="sim-input" style={{ fontSize: 12 }}
                 data-testid="msglog-filter-from" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ color: "var(--muted)", fontWeight: 700 }}>إلى (تاريخ ووقت)</span>
          <input type="datetime-local" value={toTs}
                 onChange={(e) => { setToTs(e.target.value); setPage(1); }}
                 className="sim-input" style={{ fontSize: 12 }}
                 data-testid="msglog-filter-to" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ color: "var(--muted)", fontWeight: 700 }}>اسم الحملة</span>
          <input type="text" value={campaign}
                 onChange={(e) => { setCampaign(e.target.value); setPage(1); }}
                 placeholder="مثال: winter_2026"
                 className="sim-input" style={{ fontSize: 12, minWidth: 160 }}
                 data-testid="msglog-filter-campaign" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ color: "var(--muted)", fontWeight: 700 }}>المُرسِل (اسم مستخدم)</span>
          <input type="text" value={sender}
                 onChange={(e) => { setSender(e.target.value); setPage(1); }}
                 placeholder="مثال: ceo"
                 className="sim-input" style={{ fontSize: 12, minWidth: 140 }}
                 data-testid="msglog-filter-sender" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ color: "var(--muted)", fontWeight: 700 }}>عدد الصفوف/الصفحة</span>
          <select value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="sim-input" style={{ fontSize: 12 }}
                  data-testid="msglog-page-size">
            {[50, 100, 150, 300, 500, 1000].map((n) => (
              <option key={n} value={n}>{n}{n === defaultPageSize ? " (افتراضي)" : ""}</option>
            ))}
          </select>
        </label>
        <button type="button" onClick={resetFilters}
                data-testid="msglog-reset-filters"
                style={{ padding: "6px 12px", borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "#fff", color: "var(--muted)", alignSelf: "flex-end" }}>
          إعادة ضبط الفلاتر
        </button>
      </div>

      {pending && (
        <button type="button" data-testid="msglog-new-bar"
                onClick={() => { applyPending(); if (scrollRef.current) scrollRef.current.scrollTop = 0; }}
                style={{ width: "100%", marginBottom: 8, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--brown-deep)", background: "#fff7ec", color: "var(--brown-deep)", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
          🔔 {pendingCount > 0 ? `${pendingCount} رسالة جديدة` : "توجد تحديثات جديدة"} — اضغط للتحديث
        </button>
      )}

      <div ref={scrollRef} onScroll={onTableScroll} className="card" style={{ padding: 0, overflow: "auto", maxHeight: 520 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--beige-soft)", position: "sticky", top: 0 }}>
              {["الحالة", "الاسم", "الرقم", "الرسالة", "المُرسِل", "الحملة", "أُرسلت", "وصلت", "قُرئت", ""].map((h, i) => (
                <th key={i} style={{ padding: "8px 10px", textAlign: "start", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((m, i) => {
              const st = MSG_STATUS[m.display_status] || MSG_STATUS.pending;
              return (
                <tr key={`${m.created_at || ""}|${m.phone || ""}|${i}`} style={{ borderTop: "1px solid var(--border)" }} data-testid={`msglog-row-${i}`}>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                    <span style={{ color: st.color, fontWeight: 900, fontSize: 13, letterSpacing: -1 }}>{st.icon}</span>
                    <span style={{ fontSize: 10.5, color: st.color, fontWeight: 700, marginInlineStart: 5 }}>{st.label}</span>
                    {m.display_status === "failed" && m.fail_reason && (
                      <div style={{ fontSize: 9.5, color: "#dc2626", maxWidth: 160 }}>{String(m.fail_reason).slice(0, 80)}</div>
                    )}
                  </td>
                  <td style={{ padding: "7px 10px", fontWeight: 700, whiteSpace: "nowrap" }}>{m.to_name || "—"}</td>
                  <td style={{ padding: "7px 10px", direction: "ltr", fontFamily: "monospace", fontSize: 11 }}>{m.phone}</td>
                  <td style={{ padding: "7px 10px", color: "var(--muted)", maxWidth: 260 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{m.body}</div>
                  </td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontSize: 11 }}
                      data-testid={`msglog-sender-${i}`}>
                    {m.sent_by
                      ? <span style={{ fontWeight: 700, color: "#374151" }}>{m.sent_by}</span>
                      : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>غير مسجّل</span>}
                  </td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontSize: 11 }}>
                    {m.campaign_name
                      ? <span style={{ background: "#f3e8d5", color: "var(--brown-deep)", padding: "2px 7px", borderRadius: 999, fontWeight: 700 }}>{m.campaign_name}</span>
                      : <span style={{ color: "#c8c8c8" }}>—</span>}
                  </td>
                  <td style={{ padding: "7px 10px", fontSize: 10.5, whiteSpace: "nowrap" }}>{fmtT(m.created_at)}</td>
                  <td style={{ padding: "7px 10px", fontSize: 10.5, whiteSpace: "nowrap", color: m.delivered_at ? "#333" : "#bbb" }}>{fmtT(m.delivered_at)}</td>
                  <td style={{ padding: "7px 10px", fontSize: 10.5, whiteSpace: "nowrap", color: m.read_at ? "#34B7F1" : "#bbb" }}>{fmtT(m.read_at)}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                    <button type="button" onClick={() => openRawDoc(m)}
                            disabled={rawLoading}
                            data-testid={`msglog-raw-btn-${i}`}
 
                            style={{ padding: "3px 9px", borderRadius: 6, fontSize: 10.5, fontWeight: 700, cursor: rawLoading ? "wait" : "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "#fff", color: "var(--brown-deep)" }} aria-label="عرض الوثيقة الخام من قاعدة البيانات">
                      🔍 خام
                    </button>
                  </td>
                </tr>
              );
            })}
            {!items.length && !loading && (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>لا رسائل بعد</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", marginTop: 10, fontSize: 11.5 }}>
        <button type="button" disabled={page <= 1 || loading}
                onClick={() => setPage(1)} data-testid="msglog-page-first"
                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "#fff", cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1, fontFamily: "inherit" }}>
          « الأولى
        </button>
        <button type="button" disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="msglog-page-prev"
                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "#fff", cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1, fontFamily: "inherit" }}>
          ‹ السابقة
        </button>
        <span data-testid="msglog-page-indicator" style={{ fontWeight: 700, color: "var(--brown-deep)" }}>
          صفحة {page} من {totalPages.toLocaleString()}
        </span>
        <button type="button" disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="msglog-page-next"
                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "#fff", cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1, fontFamily: "inherit" }}>
          التالية ›
        </button>
        <button type="button" disabled={page >= totalPages || loading}
                onClick={() => setPage(totalPages)} data-testid="msglog-page-last"
                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "#fff", cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1, fontFamily: "inherit" }}>
          الأخيرة »
        </button>
      </div>

      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 8 }}>
        💡 «قُرئت» تظهر فقط إذا كان العميل مفعّلاً إشعارات القراءة في واتسابه. الحالات تصل من Meta تلقائياً.
        {" · "}
        حجم الصفحة الافتراضي يُضبط من الإعدادات (<code style={{ direction: "ltr" }}>wa_message_log_page_size</code>).
      </div>

      {/* v200.15 — Raw document viewer modal */}
      {rawDoc && (
        <div data-testid="msglog-raw-modal"
             onClick={() => setRawDoc(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: zVar('extra_9999'), display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: "#fff", borderRadius: 10, maxWidth: 820, width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)" }}>🔍 وثيقة whatsapp_logs الخام</div>
              <button type="button"
                      data-testid="msglog-raw-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(rawDoc, null, 2));
                        toast.success("نُسخت الوثيقة كـ JSON");
                      }}
                      style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "#fff", color: "var(--brown-deep)" }}>
                📋 نسخ JSON
              </button>
              <button type="button"
                      data-testid="msglog-raw-close"
                      onClick={() => setRawDoc(null)}
                      style={{ marginInlineStart: "auto", padding: "4px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "#fff" }}>
                إغلاق
              </button>
            </div>
            <pre data-testid="msglog-raw-json"
                 style={{ margin: 0, padding: 16, overflow: "auto", fontSize: 11.5, direction: "ltr", textAlign: "left", fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", monospace', background: "#0b1020", color: "#e5e7eb", flex: 1 }}>
              {JSON.stringify(rawDoc, null, 2)}
            </pre>
            <div style={{ padding: "8px 16px", borderTop: "1px solid var(--border)", fontSize: 10, color: "var(--muted)" }}>
              الوثيقة مأخوذة من <code style={{ direction: "ltr" }}>db.whatsapp_logs</code> كما هي، بلا تنقية ولا إعادة تسمية. <code style={{ direction: "ltr" }}>_id</code> مُحوَّل إلى نص فقط لأن JSON لا يدعم ObjectId.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageLogPanel;
